import { fetchApi } from '@/requests/api';
import { BASE_DASHBOARD_URL } from '@/requests/constants';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '@/providers/session-provider';
import type { UserBookmarks, Folder, Bookmark } from '../types';
import { UserBookmarksSchema } from '../schemas/user-bookmark-schemas';
import { createEmptyUserBookmarks } from '../utils/bookmark-utils';
import { nanoid } from 'nanoid';

export const BOOKMARK_QUERY_KEY = 'bookmark';
export const WORK_ENGAGEMENT_BOOKMARK_QUERY_KEY = 'workengagement-bookmark';
export function useGetUserBookmarks<TData = UserBookmarks>(
  options?: Omit<
    UseQueryOptions<UserBookmarks, Error, TData>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TData, Error> {
  const {
    data: { session },
  } = useSession();

  return useQuery({
    ...options,
    queryKey: [BOOKMARK_QUERY_KEY],
    queryFn: async ({ signal }) => {
      const res = await fetchApi({
        url: `${BASE_DASHBOARD_URL}/bookmarks/user`,
        signal,
      });

      // If no bookmarks exist, return an empty object
      if (res.status === 404) {
        return {
          ...createEmptyUserBookmarks(),
          user_id: session.userId,
          customer_id: session.customerId,
        };
      }

      if (!res.ok) {
        throw new Error(`Failed to fetch user bookmarks: ${res.status}`);
      }

      const json = await res.json();
      const withClientIds = addClientIdsIfNotPresent(json);
      const data = UserBookmarksSchema.safeParse(withClientIds);

      if (!data.success) {
        throw new Error(
          `Invalid user bookmarks data: ${JSON.stringify(data.error.issues)}`
        );
      }

      return data.data;
    },
    enabled: !!session.token,
    staleTime: 60 * 5 * 1000,
    refetchOnWindowFocus: true,
  });
}

function addClientIdToBookmark(
  bookmark: Bookmark | null | undefined
): Bookmark | null | undefined {
  if (!bookmark) {
    return bookmark;
  }
  if (bookmark.client_id) {
    return bookmark;
  }
  return { ...bookmark, client_id: nanoid() };
}

function addClientIdToFolder(
  folder: Folder | null | undefined
): Folder | null | undefined {
  if (!folder) {
    return folder;
  }

  const updatedFolder: Folder = folder.client_id
    ? { ...folder }
    : { ...folder, client_id: nanoid() };

  if (updatedFolder.child_bookmarks?.length) {
    updatedFolder.child_bookmarks = updatedFolder.child_bookmarks
      .map(addClientIdToBookmark)
      .filter((b): b is Bookmark => b != null);
  }

  if (updatedFolder.child_folders?.length) {
    updatedFolder.child_folders = updatedFolder.child_folders
      .map(addClientIdToFolder)
      .filter((f): f is Folder => f != null);
  }

  return updatedFolder;
}

function addClientIdsIfNotPresent(data: UserBookmarks): UserBookmarks {
  return {
    ...data,
    bookmarks: (data.bookmarks ?? [])
      .map(addClientIdToBookmark)
      .filter((b): b is Bookmark => b != null),
    folders: (data.folders ?? [])
      .map(addClientIdToFolder)
      .filter((f): f is Folder => f != null),
    folders_shared_with_me: (data.folders_shared_with_me ?? [])
      .map(addClientIdToFolder)
      .filter((f): f is Folder => f != null),
  };
}

export function useGetBookmarks(
  options?: Omit<
    UseQueryOptions<UserBookmarks, Error, Bookmark[]>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<Bookmark[], Error> {
  return useGetUserBookmarks({
    ...options,
    select: (data) => data.bookmarks,
  });
}

export function useGetFolders(
  options?: Omit<
    UseQueryOptions<UserBookmarks, Error, Folder[]>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<Folder[], Error> {
  return useGetUserBookmarks({
    ...options,
    select: (data) => data.folders,
  });
}
